import React from 'react';
import { Text, VStack } from '@gluestack-ui/themed';
import { useVoiceAssistant } from '@livekit/components-react';
import type { AgentState } from '@livekit/components-react';
import { useTranslation } from 'react-i18next';

import { AIPresenceOrb } from '@/common/components/presence/AIPresenceOrb';
import { computeSpeakingPulseOverhangPx } from '@/common/components/presence/presenceGlowBudget';
import type { AIPresenceState, PresenceTrackRef } from '@/common/components/presence/types';

type PresenceStatusColor = '$textBrand' | '$textSubtle' | '$statusError';

type PresenceStatusLabel = {
  copyKey: string;
  defaultValue: string;
  color: PresenceStatusColor;
};

type AiExaminerPresenceViewProps = {
  size: number;
  voiceAssistantState?: AgentState | null;
  trackRef?: PresenceTrackRef;
  showStatusLabel?: boolean;
  /** Forces orb/caption (e.g. examiner-unavailable → error / Reconnecting…). */
  presenceOverride?: AIPresenceState | null;
  preferListeningWhenSilent?: boolean;
};

export type AiExaminerPresenceProps = {
  size?: number;
  /** Injected voice-assistant state — used when not calling the live hook. */
  voiceAssistantState?: AgentState | null;
  /** Injected agent audio track — used when not calling the live hook. */
  trackRef?: PresenceTrackRef;
  /** When false, hides the caption under the orb (default true). */
  showStatusLabel?: boolean;
  /**
   * When true, reads `useVoiceAssistant()` from the LiveKit room context.
   * Must only be set under `MeetingSessionShell` (cohort in-progress). Off
   * by default so wrap-up and unit tests get the idle breathing fallback.
   */
  useLiveVoiceAssistant?: boolean;
  /** Forces orb/caption (e.g. examiner-unavailable → error / Reconnecting…). */
  presenceOverride?: AIPresenceState | null;
  /**
   * After the examiner has read the question, LiveKit often sits in
   * ``thinking`` with no audio. Product copy is Listening.
   */
  preferListeningWhenSilent?: boolean;
};

/** Maps LiveKit voice-assistant state to the unified presence vocabulary. */
export function mapVoiceAssistantStateToOrbState(
  voiceState?: AgentState | null,
): AIPresenceState {
  if (voiceState === 'speaking') return 'speaking';
  if (voiceState === 'listening') return 'listening';
  if (
    voiceState === 'thinking'
    || voiceState === 'initializing'
    || voiceState === 'connecting'
  ) {
    return 'thinking';
  }
  return 'idle';
}

export function hasLiveAgentAudioTrack(trackRef?: PresenceTrackRef | null): boolean {
  return Boolean(trackRef?.publication?.track?.mediaStreamTrack);
}

/**
 * Resolves orb state for the examiner — audible agent audio wins over a lagging
 * `thinking` voice-assistant signal so glow/envelope read as speaking.
 */
export function resolveExaminerOrbState(
  voiceState?: AgentState | null,
  trackRef?: PresenceTrackRef | null,
  opts?: { preferListeningWhenSilent?: boolean },
): AIPresenceState {
  const mapped = mapVoiceAssistantStateToOrbState(voiceState);
  if (mapped === 'thinking' && opts?.preferListeningWhenSilent) {
    return 'listening';
  }
  if (mapped === 'thinking' && hasLiveAgentAudioTrack(trackRef)) {
    return 'speaking';
  }
  return mapped;
}

/** Maps unified presence state to the under-orb status caption (copy + token). */
export function mapPresenceStateToStatusLabel(state: AIPresenceState): PresenceStatusLabel {
  switch (state) {
    case 'listening':
      return {
        copyKey: 'oralExam.student.presence.listening',
        defaultValue: 'Listening',
        color: '$textSubtle',
      };
    case 'speaking':
      return {
        copyKey: 'oralExam.student.presence.speaking',
        defaultValue: 'Speaking',
        color: '$textBrand',
      };
    case 'thinking':
      return {
        copyKey: 'oralExam.student.presence.thinking',
        defaultValue: 'Thinking…',
        color: '$textSubtle',
      };
    case 'error':
      return {
        copyKey: 'oralExam.student.presence.error',
        defaultValue: 'Reconnecting…',
        color: '$statusError',
      };
    case 'ready':
    case 'idle':
    default:
      return {
        copyKey: 'oralExam.student.presence.ready',
        defaultValue: 'Ready',
        color: '$textSubtle',
      };
  }
}

function AiExaminerPresenceView({
  size,
  voiceAssistantState,
  trackRef = null,
  showStatusLabel = true,
  presenceOverride = null,
  preferListeningWhenSilent = false,
}: AiExaminerPresenceViewProps) {
  const { t } = useTranslation();
  const orbState = presenceOverride
    ?? resolveExaminerOrbState(voiceAssistantState, trackRef, {
      preferListeningWhenSilent,
    });
  const statusLabel = mapPresenceStateToStatusLabel(orbState);

  return (
    <VStack alignItems="center" space="xs" testID="oral-exam-ai-orb">
      <AIPresenceOrb
        state={orbState}
        size={size}
        trackRef={trackRef}
        testID="oral-exam-ai-orb-visual"
      />
      {showStatusLabel ? (
        <Text
          testID="oral-exam-ai-orb-status"
          mt={computeSpeakingPulseOverhangPx(size)}
          size="xs"
          fontWeight="semibold"
          textTransform="uppercase"
          letterSpacing={1}
          textAlign="center"
          color={statusLabel.color}
        >
          {t(statusLabel.copyKey, { defaultValue: statusLabel.defaultValue })}
        </Text>
      ) : null}
    </VStack>
  );
}

function LiveVoiceAiExaminerPresence({
  size,
  showStatusLabel,
  presenceOverride,
  preferListeningWhenSilent,
}: {
  size: number;
  showStatusLabel?: boolean;
  presenceOverride?: AIPresenceState | null;
  preferListeningWhenSilent?: boolean;
}) {
  const { state, audioTrack } = useVoiceAssistant();

  return (
    <AiExaminerPresenceView
      size={size}
      voiceAssistantState={state}
      trackRef={audioTrack ?? null}
      showStatusLabel={showStatusLabel}
      presenceOverride={presenceOverride}
      preferListeningWhenSilent={preferListeningWhenSilent}
    />
  );
}

/**
 * AI examiner orb — voice-reactive under LiveKit room context, idle fallback elsewhere.
 */
export function AiExaminerPresence({
  size = 160,
  voiceAssistantState,
  trackRef,
  showStatusLabel = true,
  useLiveVoiceAssistant = false,
  presenceOverride = null,
  preferListeningWhenSilent = false,
}: AiExaminerPresenceProps) {
  if (useLiveVoiceAssistant) {
    return (
      <LiveVoiceAiExaminerPresence
        size={size}
        showStatusLabel={showStatusLabel}
        presenceOverride={presenceOverride}
        preferListeningWhenSilent={preferListeningWhenSilent}
      />
    );
  }

  return (
    <AiExaminerPresenceView
      size={size}
      voiceAssistantState={voiceAssistantState}
      trackRef={trackRef ?? null}
      showStatusLabel={showStatusLabel}
      presenceOverride={presenceOverride}
      preferListeningWhenSilent={preferListeningWhenSilent}
    />
  );
}
